import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Users, Zap, Monitor, Star, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";

export default function LandingPage() {
  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Random Pairing",
      description: "Automatically generate Jack & Jill pairs for each round with our smart pairing algorithm."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Live Scoring",
      description: "Judges score dancers in real-time from their mobile phones with our ultra-fast interface."
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: "Projector Display",
      description: "Professional live display for audiences showing current heat, rankings, and results."
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      title: "Instant Rankings",
      description: "Automatic calculation of individual dancer rankings for Leaders and Followers."
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/jj-logo.png" alt="J&J" className="w-9 h-9 rounded-lg object-cover" />
            <span className="font-['Playfair_Display'] text-xl font-bold text-white">JackAndJill</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/judge-login" className="text-[#A1A1AA] text-xs sm:text-sm hover:text-white transition-colors" data-testid="nav-judge-login">
              Judge
            </Link>
            <Link to="/audience" className="text-[#A1A1AA] text-xs sm:text-sm hover:text-white transition-colors" data-testid="nav-audience">
              Audience
            </Link>
            <Link to="/login" className="text-[#A1A1AA] text-xs sm:text-sm hover:text-white transition-colors hidden sm:block" data-testid="nav-login">
              Sign In
            </Link>
            <Link to="/register" data-testid="nav-register">
              <Button className="btn-gold px-4 sm:px-6 py-2 rounded-sm text-sm">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1596287453420-52ce4a949ca9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb3VwbGUlMjBkYW5jaW5nJTIwc3BvdGxpZ2h0JTIwZHJhbWF0aWMlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcyODA4MDQ1fDA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/70 via-[#09090b]/80 to-[#09090b]" />
        <div className="absolute inset-0 stage-light" />

        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between gap-8">
            {/* Left: Text content */}
            <motion.div
              className="flex-1 text-left"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-wider text-[#FFD700] bg-[#FFD700]/10 rounded-full border border-[#FFD700]/20">
                DANCE COMPETITION MANAGEMENT
              </span>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
                Where Every Step <br />
                <span className="text-[#FFD700]">Counts</span>
              </h1>
              <p className="text-lg sm:text-xl text-[#A1A1AA] mb-10 max-w-xl">
                The ultimate platform for Jack & Jill dance competitions. 
                Run festivals, score dancers live, and deliver stunning results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register" data-testid="hero-get-started">
                  <Button className="btn-gold px-8 py-4 text-lg rounded-sm w-full sm:w-auto">
                    Start Your Festival
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-signin">
                  <Button variant="outline" className="btn-outline px-8 py-4 text-lg rounded-sm w-full sm:w-auto">
                    Sign In
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right: J&J Logo */}
            <motion.div
              className="hidden md:flex items-center justify-center flex-shrink-0"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <img 
                src="/jj-logo.png" 
                alt="JackAndJillScore" 
                className="w-56 lg:w-72 xl:w-80 drop-shadow-[0_0_40px_rgba(255,215,0,0.3)]" 
              />
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-[#A1A1AA]/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#FFD700] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              Built for dance festival organizers who demand excellence and precision.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="glass p-8 rounded-lg card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#FFD700]/10 rounded-lg flex items-center justify-center text-[#FFD700] mb-6">
                  {feature.icon}
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#A1A1AA]">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dance Gallery Section */}
      <section className="py-24 px-6 bg-[#18181b]/50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#FF007F] font-medium tracking-wider text-sm mb-4 block">
              THE MAGIC OF JACK & JILL
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Where Dance Meets Destiny
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
              Random pairing, instant connection. Watch the magic unfold on the dance floor.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              className="relative group rounded-2xl overflow-hidden aspect-[4/5]"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1767784097233-f9d05d6cabe9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxjb3VwbGUlMjBkYW5jaW5nJTIwZWxlZ2FudCUyMGJhbGxyb29tJTIwcm9tYW50aWN8ZW58MHx8fHwxNzcyOTkwMzU4fDA&ixlib=rb-4.1.0&q=85"
                alt="Couple dancing intimately"
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[#FFD700] text-sm font-medium mb-1">Connection</p>
                <p className="text-white text-lg font-bold">Feel the rhythm together</p>
              </div>
            </motion.div>

            <motion.div
              className="relative group rounded-2xl overflow-hidden aspect-[4/5]"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1769245524263-77ece02f64d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxkYW5jZSUyMGZsb29yJTIwZXZlbnQlMjBwZW9wbGUlMjBkYW5jaW5nJTIwdG9nZXRoZXIlMjBuaWdodHxlbnwwfHx8fDE3NzI5OTAzNTh8MA&ixlib=rb-4.1.0&q=85"
                alt="Couple dancing tango at social event"
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[#FF007F] text-sm font-medium mb-1">Passion</p>
                <p className="text-white text-lg font-bold">Every step tells a story</p>
              </div>
            </motion.div>

            <motion.div
              className="relative group rounded-2xl overflow-hidden aspect-[4/5]"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1760428300151-f4702f39fe83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxjb3VwbGUlMjBkYW5jaW5nJTIwZWxlZ2FudCUyMGJhbGxyb29tJTIwcm9tYW50aWN8ZW58MHx8fHwxNzcyOTkwMzU4fDA&ixlib=rb-4.1.0&q=85"
                alt="Couples dancing tango in ballroom"
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[#00E5FF] text-sm font-medium mb-1">Community</p>
                <p className="text-white text-lg font-bold">Dance floor magic</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Dancing Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1767784097135-0d89bf307e1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxkYW5jZSUyMGZsb29yJTIwZXZlbnQlMjBwZW9wbGUlMjBkYW5jaW5nJTIwdG9nZXRoZXIlMjBuaWdodHxlbnwwfHx8fDE3NzI5OTAzNTh8MA&ixlib=rb-4.1.0&q=85"
            alt="Couples dancing on floor"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/90 to-[#09090b]/70" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FFD700] font-medium tracking-wider text-sm mb-4 block">
                LIVE EVENTS
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-6">
                The Thrill of Social Dancing
              </h2>
              <p className="text-[#A1A1AA] text-lg mb-8">
                Experience the energy of dozens of couples moving together in perfect harmony. 
                Our platform handles competitions up to 35 couples per heat seamlessly.
              </p>
              <ul className="space-y-4">
                {[
                  "Up to 35 couples (70 dancers) per heat",
                  "Real-time scoring visible to all judges",
                  "Live leaderboard for audiences",
                  "Instant results after each round"
                ].map((item, i) => (
                  <li key={item} className="flex items-center gap-3 text-[#FAFAFA]">
                    <Star className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <div></div>
          </div>
        </div>
      </section>

      {/* Judge Interface Preview */}
      <section className="py-24 px-6 bg-[#18181b]/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FF007F] font-medium tracking-wider text-sm mb-4 block">
                MOBILE-FIRST DESIGN
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-6">
                Lightning-Fast Judge Interface
              </h2>
              <p className="text-[#A1A1AA] text-lg mb-8">
                Designed for real-world competition pressure. Large touch targets, 
                instant feedback, and seamless scoring during live performances.
              </p>
              <ul className="space-y-4">
                {[
                  "Score Musicality, Technique, Connection & Presence",
                  "Optimized for one-handed mobile use",
                  "Instant visual feedback on score changes",
                  "Automatic progression to next heat"
                ].map((item, i) => (
                  <li key={item} className="flex items-center gap-3 text-[#FAFAFA]">
                    <Star className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="glass-heavy rounded-2xl p-6 max-w-sm mx-auto">
                <div className="bg-[#09090b] rounded-xl p-4 mb-4">
                  <div className="text-center mb-4">
                    <span className="text-[#FF007F] text-xs font-semibold tracking-wider">HEAT 5 OF 12</span>
                    <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mt-1">Carlos M.</h3>
                    <p className="text-[#A1A1AA] text-sm">Leader</p>
                  </div>
                  <div className="space-y-4">
                    {["Musicality", "Technique", "Connection", "Presence"].map((label, i) => (
                      <div key={label}>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-[#A1A1AA]">{label}</span>
                          <span className="text-[#FFD700] font-bold">{7 + i * 0.5}</span>
                        </div>
                        <div className="h-3 bg-[#27272a] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#FFD700] to-[#E5C100] rounded-full"
                            style={{ width: `${(7 + i * 0.5) * 10}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Button className="w-full btn-gold py-4 text-lg rounded-lg">
                  Submit Score
                </Button>
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 -z-10 blur-3xl opacity-30 bg-gradient-to-r from-[#FFD700] to-[#FF007F]" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Organizers Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="relative order-2 lg:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1758613171720-74768b04e2a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMGZyaWVuZHMlMjBzbWlsaW5nJTIwaGFwcHklMjBwb3J0cmFpdCUyMGdyb3VwfGVufDB8fHx8MTc3Mjk5MDM1Mnww&ixlib=rb-4.1.0&q=85"
                  alt="Happy young organizers"
                  className="w-full h-auto rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b]/50 to-transparent rounded-2xl" />
              </div>
            </motion.div>
            
            <motion.div
              className="order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#00E5FF] font-medium tracking-wider text-sm mb-4 block">
                FOR ORGANIZERS
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-6">
                Run Events Like a Pro
              </h2>
              <p className="text-[#A1A1AA] text-lg mb-8">
                Join successful organizers who trust JackAndJill to power their competitions. 
                From small workshops to major festivals, we've got you covered.
              </p>
              <ul className="space-y-4">
                {[
                  "Easy setup - get started in minutes",
                  "CSV import for bulk dancer registration",
                  "Automated heat generation",
                  "PDF results export"
                ].map((item, i) => (
                  <li key={item} className="flex items-center gap-3 text-[#FAFAFA]">
                    <Star className="w-5 h-5 text-[#00E5FF] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="inline-block mt-8">
                <Button className="btn-gold px-8 py-4 text-lg rounded-sm">
                  Start Organizing
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple Pricing
            </h2>
            <p className="text-[#A1A1AA] text-base sm:text-lg mb-12">
              Pay per event. No subscriptions, no hidden fees.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { price: "130€", dancers: "Up to 50 dancers", label: "Starter Event", accent: "#00E5FF" },
              { price: "200€", dancers: "Up to 100 dancers", label: "Standard Event", accent: "#FFD700", popular: true },
              { price: "2€", dancers: "100–300 dancers", label: "Large Events", accent: "#FF007F", perDancer: true }
            ].map((plan, i) => (
              <motion.div
                key={plan.label}
                className={`glass p-8 rounded-2xl relative ${plan.popular ? 'ring-2 ring-[#FFD700]' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`pricing-card-${i}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD700] text-black text-xs font-bold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="text-center">
                  <span className="text-sm font-medium" style={{ color: plan.accent }}>{plan.label}</span>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    <span className="font-['Playfair_Display'] text-5xl font-bold text-white">{plan.price}</span>
                    {plan.perDancer && <span className="text-sm text-[#A1A1AA]">/ dancer</span>}
                  </div>
                  <p className="text-[#A1A1AA] text-sm mt-2">{plan.dancers}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Included list */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass p-8 rounded-2xl mb-8"
            data-testid="pricing-included-card"
          >
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-white mb-5">Included in every plan</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Live scoring system",
                "Judge mobile access",
                "Live rankings",
                "Cinematic presentation",
                "Public results",
                "Heat management",
                "PDF / Excel exports",
                "Audience voting support (optional)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-[#FAFAFA] text-sm">
                  <div className="w-5 h-5 rounded-full bg-[#FFD700]/15 flex items-center justify-center flex-shrink-0">
                    <Star className="w-3 h-3 text-[#FFD700]" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Optional on-site management */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass p-8 rounded-2xl mb-8 border border-[#FF007F]/30"
            data-testid="pricing-onsite-card"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-white">
                Optional On-Site Live Management
              </h3>
              <span className="font-['Playfair_Display'] text-3xl font-bold text-[#FF007F]">+200€</span>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-2">(plus accommodation &amp; travel expenses)</p>
            <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40">
              <Star className="w-3.5 h-3.5 text-[#FFD700]" />
              <span className="text-[#FFD700] text-xs font-bold uppercase tracking-wider">
                Mandatory for your first event
              </span>
            </div>
            <p className="text-white text-sm mb-3">This includes:</p>
            <ul className="space-y-2 mb-4">
              {[
                "Full live system operation during the event",
                "Technical support — Real-time assist*",
                "Competition flow management",
                "Live ranking supervision",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#FAFAFA] text-sm">
                  <div className="w-5 h-5 rounded-full bg-[#FF007F]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Star className="w-3 h-3 text-[#FF007F]" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-[#71717A] text-xs italic leading-relaxed border-t border-white/5 pt-3">
              * In the event of a page crash, the technical support team is not liable. Technical support will do
              everything possible to resolve the issue within a reasonable timeframe.
            </p>
          </motion.div>

          {/* Sponsor note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
            data-testid="pricing-sponsor-note"
          >
            <p className="text-[#A1A1AA] text-sm max-w-2xl mx-auto leading-relaxed">
              For festivals who want to have <span className="text-[#FFD700] font-semibold">J&amp;J sponsors</span> to pay,
              please contact us via Instagram{' '}
              <a
                href="https://instagram.com/jackandjillscore"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FF007F] font-semibold hover:underline"
              >
                @jackandjillscore
              </a>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-[#FFD700]" />
            <span className="font-['Playfair_Display'] font-bold text-white">JackAndJill</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/judge-login" className="text-[#52525B] text-sm hover:text-[#A1A1AA] transition-colors" data-testid="judge-login-link">
              Judge Login
            </Link>
            <Link to="/audience" className="text-[#52525B] text-sm hover:text-[#A1A1AA] transition-colors" data-testid="audience-portal-link">
              Audience Voting
            </Link>
            <Link to="/terms" className="text-[#52525B] text-sm hover:text-[#A1A1AA] transition-colors" data-testid="terms-link">
              Terms of Use
            </Link>
            <p className="text-[#52525B] text-sm">
              © 2024 JackAndJillScore — All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
